// ============================================================================
// CONFIG SETTINGS SCREEN
// ============================================================================
// Two-panel form-based editor for the Configuration Settings module.
// Left: group sidebar. Right: scope selector + settings form + save/discard footer.
// Ported from crs-web/src/components/modules/config-module/ConfigSettingsScreen.tsx
// and adapted to PRIMA's SYSTEM → OFFICE → EMPLOYEE scope hierarchy.
//
// Bound to the left sidebar via:
//  - components/common/ComponentRegistry.tsx → COMPONENT_REGISTRY['ConfigSettingsScreen']
//  - Layout/main_layout.tsx → ENTERPRISE_LAYOUT_CONFIG.navigation + .modules['config-settings']

import React, { useState, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { useConfigSettings } from 'hooks/api/useConfigSettings';
import type { ScopeType } from 'hooks/api/useConfigSettings';
import ConfigGroupSidebar from './ConfigGroupSidebar';
import ConfigSettingsPanel from './ConfigSettingsPanel';

interface ConfigSettingsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ConfigSettingsScreen: React.FC<ConfigSettingsScreenProps> = () => {
  const {
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedOfficeId,
    selectedEmployeeId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    selectGroup,
    changeScopeType,
    selectOffice,
    selectEmployee,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  } = useConfigSettings();

  // Pending navigation when unsaved changes exist
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Save and discard confirmation dialogs
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const activeGroup = groups.find((g) => g.groupKey === activeGroupKey) ?? null;

  // Guard group selection with unsaved changes check
  const handleGroupSelect = useCallback(
    (groupKey: string) => {
      if (groupKey === activeGroupKey) return;
      if (hasChanges) {
        pendingActionRef.current = () => selectGroup(groupKey);
        setConfirmOpen(true);
      } else {
        selectGroup(groupKey);
      }
    },
    [activeGroupKey, hasChanges, selectGroup]
  );

  // Guard scope change with unsaved changes check
  const handleScopeTypeChange = useCallback(
    (newScope: ScopeType) => {
      if (newScope === scopeType) return;
      if (hasChanges) {
        pendingActionRef.current = () => changeScopeType(newScope);
        setConfirmOpen(true);
      } else {
        changeScopeType(newScope);
      }
    },
    [scopeType, hasChanges, changeScopeType]
  );

  // Guard office change with unsaved changes check
  const handleOfficeSelect = useCallback(
    (officeId: string | null) => {
      if (officeId === selectedOfficeId) return;
      if (hasChanges) {
        pendingActionRef.current = () => selectOffice(officeId);
        setConfirmOpen(true);
      } else {
        selectOffice(officeId);
      }
    },
    [selectedOfficeId, hasChanges, selectOffice]
  );

  // Guard employee change with unsaved changes check
  const handleEmployeeSelect = useCallback(
    (employeeId: string | null) => {
      if (employeeId === selectedEmployeeId) return;
      if (hasChanges) {
        pendingActionRef.current = () => selectEmployee(employeeId);
        setConfirmOpen(true);
      } else {
        selectEmployee(employeeId);
      }
    },
    [selectedEmployeeId, hasChanges, selectEmployee]
  );

  // Confirm discard
  const handleConfirmDiscard = useCallback(() => {
    discardChanges();
    setConfirmOpen(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, [discardChanges]);

  const handleCancelDiscard = useCallback(() => {
    setConfirmOpen(false);
    pendingActionRef.current = null;
  }, []);

  // Save confirmation
  const handleSaveClick = useCallback(() => {
    setSaveConfirmOpen(true);
  }, []);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    saveChanges();
  }, [saveChanges]);

  const handleCancelSave = useCallback(() => {
    setSaveConfirmOpen(false);
  }, []);

  // Discard confirmation
  const handleDiscardClick = useCallback(() => {
    setDiscardConfirmOpen(true);
  }, []);

  const handleConfirmDiscardChanges = useCallback(() => {
    setDiscardConfirmOpen(false);
    discardChanges();
  }, [discardChanges]);

  const handleCancelDiscardChanges = useCallback(() => {
    setDiscardConfirmOpen(false);
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Configuration Settings"
        description="Manage business policy settings: timesheet rules, engagement defaults, system parameters."
        icon={<IconSystem.settings size={18} />}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="config-settings-container">
          <ConfigGroupSidebar
            groups={groups}
            activeGroupKey={activeGroupKey}
            onGroupSelect={handleGroupSelect}
            loading={groupsLoading}
          />
          <ConfigSettingsPanel
            activeGroup={activeGroup}
            scopeType={scopeType}
            onScopeTypeChange={handleScopeTypeChange}
            selectedOfficeId={selectedOfficeId}
            onOfficeSelect={handleOfficeSelect}
            selectedEmployeeId={selectedEmployeeId}
            onEmployeeSelect={handleEmployeeSelect}
            effectiveValues={effectiveValues}
            definitions={definitions}
            editedValues={editedValues}
            onValueChange={updateValue}
            onResetValue={resetValue}
            onSave={handleSaveClick}
            onDiscard={handleDiscardClick}
            hasChanges={hasChanges}
            saving={saving}
            loading={loading}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        variant="danger"
      />

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Changes"
        message="Are you sure you want to save the configuration changes?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
        variant="default"
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes"
        message="Are you sure you want to discard all unsaved changes?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDiscardChanges}
        onCancel={handleCancelDiscardChanges}
        variant="danger"
      />
    </div>
  );
};

export default ConfigSettingsScreen;
